import { html, css, PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

export type BannerVariant = 'warning' | 'info' | 'error';

const VARIANT_GRADIENTS: Record<BannerVariant, string> = {
    warning: 'linear-gradient(135deg, #f59e0b, #d97706)',
    info: 'linear-gradient(135deg, #2563eb, #1e40af)',
    error: 'linear-gradient(135deg, #ef4444, #dc2626)',
};

/**
 * A notification banner that slides in from the top of the viewport.
 *
 * @fires banner-dismiss - Fired when auto-dismiss timer expires
 */
@customElement('notification-banner')
export class NotificationBanner extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: block;
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 10000;
                transform: translateY(-100%);
                transition: transform 0.3s ease-in-out;
                pointer-events: none;
            }

            :host([visible]) {
                transform: translateY(0);
                pointer-events: auto;
            }

            .banner {
                padding: 0.75rem 1rem;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.5rem;
                font-weight: 500;
                color: #fff;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
            }

            .icon {
                font-size: 1.25rem;
            }

            .message {
                font-size: 0.95rem;
            }
        `,
    ];

    @property({ type: String, reflect: true })
    variant: BannerVariant = 'warning';

    @property({ type: Boolean, reflect: true })
    visible = false;

    @property({ type: String })
    message = '';

    @property({ type: String })
    icon = '';

    @property({ type: Number, attribute: 'auto-dismiss' })
    autoDismiss = 0;

    private _dismissTimer: ReturnType<typeof setTimeout> | null = null;

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this._clearTimer();
    }

    updated(changed: PropertyValues): void {
        if (changed.has('visible')) {
            this._clearTimer();
            if (this.visible && this.autoDismiss > 0) {
                this._dismissTimer = setTimeout(() => {
                    this.visible = false;
                    this.dispatchEvent(
                        new CustomEvent('banner-dismiss', {
                            bubbles: true,
                            composed: true,
                        })
                    );
                }, this.autoDismiss);
            }
        }
    }

    private _clearTimer(): void {
        if (this._dismissTimer !== null) {
            clearTimeout(this._dismissTimer);
            this._dismissTimer = null;
        }
    }

    private get _defaultIcon(): string {
        switch (this.variant) {
            case 'warning': return '\u26A0\uFE0F';
            case 'info': return '\uD83D\uDCE1';
            case 'error': return '\u274C';
        }
    }

    render() {
        const gradient = VARIANT_GRADIENTS[this.variant] ?? VARIANT_GRADIENTS.warning;
        const icon = this.icon || this._defaultIcon;

        return html`
            <div class="banner" style="background: ${gradient}">
                <span class="icon">${icon}</span>
                <span class="message">${this.message}</span>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'notification-banner': NotificationBanner;
    }
}
